export const MOBILE_INPUT_FOCUS_SUPPRESSION_QUERY = '(pointer: coarse) and (hover: none)';
export const SKIP_AUTO_FOCUS_ATTRIBUTE = 'data-skip-auto-focus';

type NavigatorWithMobileHint = Pick<Navigator, 'maxTouchPoints' | 'userAgent'> & {
  userAgentData?: { mobile?: boolean };
};

export type AutoFocusableInput = HTMLInputElement | HTMLTextAreaElement;

const MOBILE_USER_AGENT_PATTERN =
  /Android|BlackBerry|BB10|iPad|iPhone|iPod|Mobile|Windows Phone/i;

const EXCLUDED_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

const DROPDOWN_INPUT_SELECTOR = [
  `[${SKIP_AUTO_FOCUS_ATTRIBUTE}="true"]`,
  '[role="combobox"]',
  '[aria-haspopup="listbox"]',
  '[aria-haspopup="menu"]',
  '.MuiAutocomplete-root',
  '.MuiSelect-root',
  '.MuiSelect-select',
  '.MuiSelect-nativeInput',
].join(', ');

function isDropdownBacked(element: HTMLElement): boolean {
  return element.matches(DROPDOWN_INPUT_SELECTOR) || !!element.closest(DROPDOWN_INPUT_SELECTOR);
}

export function isMobileNavigator(
  nav: NavigatorWithMobileHint | undefined =
    typeof navigator === 'undefined' ? undefined : navigator,
): boolean {
  if (!nav) return false;

  if (typeof nav.userAgentData?.mobile === 'boolean') {
    return nav.userAgentData.mobile;
  }

  return (
    MOBILE_USER_AGENT_PATTERN.test(nav.userAgent) ||
    (/Macintosh/i.test(nav.userAgent) && nav.maxTouchPoints > 1)
  );
}

export function canAutoFocusInputNow(
  win: Pick<Window, 'matchMedia'> | undefined =
    typeof window === 'undefined' ? undefined : window,
  nav: NavigatorWithMobileHint | undefined =
    typeof navigator === 'undefined' ? undefined : navigator,
): boolean {
  const hasPhonePointer =
    !!win &&
    typeof win.matchMedia === 'function' &&
    win.matchMedia(MOBILE_INPUT_FOCUS_SUPPRESSION_QUERY).matches;

  return (
    !!win &&
    !hasPhonePointer &&
    !isMobileNavigator(nav)
  );
}

function hasHiddenAncestor(element: HTMLElement): boolean {
  return !!element.closest('[hidden], [aria-hidden="true"], [inert]');
}

export function isElementVisible(element: HTMLElement): boolean {
  const win = element.ownerDocument.defaultView;
  if (!win || hasHiddenAncestor(element)) return false;

  const style = win.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') {
    return false;
  }

  return element.getClientRects().length > 0;
}

export function isAutoFocusableEmptyInput(element: Element): element is AutoFocusableInput {
  if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
    return false;
  }

  if (element.disabled || element.readOnly || element.tabIndex < 0) return false;

  if (element instanceof HTMLInputElement && EXCLUDED_INPUT_TYPES.has(element.type)) {
    return false;
  }

  if (element instanceof HTMLInputElement && element.list) return false;

  if (isDropdownBacked(element)) return false;

  return element.value.trim() === '' && isElementVisible(element);
}

export function findFirstEmptyAutoFocusableInput(scope: ParentNode): AutoFocusableInput | null {
  const candidates = [
    ...(scope instanceof Element && scope.matches('input, textarea') ? [scope] : []),
    ...Array.from(scope.querySelectorAll('input, textarea')),
  ];

  return candidates.find(isAutoFocusableEmptyInput) ?? null;
}

function getLastVisibleSurface(doc: Document): HTMLElement | null {
  const surfaces = Array.from(
    doc.querySelectorAll<HTMLElement>(
      '[role="dialog"], [role="alertdialog"], .MuiModal-root, .MuiPopover-root',
    ),
  ).filter(isElementVisible);

  return surfaces[surfaces.length - 1] ?? null;
}

export function getInputFocusScope(doc: Document = document): ParentNode | null {
  return getLastVisibleSurface(doc) ?? doc.getElementById('root') ?? doc.body;
}

function scopeContainsElement(scope: ParentNode | null | undefined, element: HTMLElement): boolean {
  return scope instanceof Node && (scope === element || scope.contains(element));
}

/** True when the user is already in a still-visible text field we must not yank away from.
 *  Buttons and dropdowns/selects are excluded: those are the controls that typically reveal
 *  a new set of inputs, and the first empty field in that new UI should take focus. */
function isPreservedFocusTarget(element: Element | null | undefined): boolean {
  if (!(element instanceof HTMLElement) || !isElementVisible(element) || isDropdownBacked(element)) {
    return false;
  }

  if (element instanceof HTMLTextAreaElement) {
    return !element.disabled && !element.readOnly;
  }

  if (element instanceof HTMLInputElement) {
    return !element.disabled && !element.readOnly && !EXCLUDED_INPUT_TYPES.has(element.type);
  }

  return element.isContentEditable || element.matches('[role="textbox"], [role="searchbox"]');
}

export function hasDefaultFocus(
  doc: Document = document,
  scope?: ParentNode | null,
  ignoreFocusOutsideScope = false,
): boolean {
  const activeElement = doc.activeElement;
  if (
    ignoreFocusOutsideScope &&
    activeElement instanceof HTMLElement &&
    !scopeContainsElement(scope, activeElement) &&
    !isPreservedFocusTarget(activeElement)
  ) {
    return false;
  }

  return (
    activeElement instanceof HTMLElement &&
    activeElement !== doc.body &&
    activeElement !== doc.documentElement
  );
}

export function focusInputOnDesktop(input: AutoFocusableInput | null | undefined): boolean {
  const win = input?.ownerDocument.defaultView ?? undefined;
  if (!input || !canAutoFocusInputNow(win, win?.navigator)) {
    return false;
  }

  if (!isAutoFocusableEmptyInput(input)) return false;

  input.focus({ preventScroll: true });
  return input.ownerDocument.activeElement === input;
}

export interface FocusFirstEmptyInputOptions {
  scope?: ParentNode | null;
  ignoreFocusOutsideScope?: boolean;
}

export function focusFirstEmptyInputIfAppropriate(
  doc: Document = document,
  options: FocusFirstEmptyInputOptions = {},
): boolean {
  const win = doc.defaultView ?? undefined;
  if (!canAutoFocusInputNow(win, win?.navigator)) return false;
  if (hasDefaultFocus(doc, options.scope, options.ignoreFocusOutsideScope)) return false;

  const scope = options.scope ?? getInputFocusScope(doc);
  if (!scope) return false;

  const input = findFirstEmptyAutoFocusableInput(scope);
  if (!input) return false;

  input.focus({ preventScroll: true });
  return doc.activeElement === input;
}
